import { defineNode, Editor, NodeInterface, type AbstractNode } from "@baklavajs/core"
import { setNodePosition } from "@baklavajs/renderer-vue"
import type { VisualPipelineBlock, VisualPipelineGraph } from "./visual-pipeline-block"

export type VisualPipelineBaklavaField = {
  key: string
  label: string
  type: string
  value: unknown
}

export type VisualPipelineBaklavaNode = {
  id: string
  type: string
  label: string
  category?: string
  position: { x: number; y: number }
  hasInput: boolean
  hasOutput: boolean
  fields: VisualPipelineBaklavaField[]
}

export type VisualPipelineBaklavaEdge = {
  id: string
  from: string
  to: string
}

export type VisualPipelineBaklavaHydrationModel = {
  nodes: VisualPipelineBaklavaNode[]
  edges: VisualPipelineBaklavaEdge[]
  warnings: string[]
}

export type BaklavaHydrationResult = {
  warnings: string[]
  nodeIds: string[]
  edgeIds: string[]
}

function fieldLabel(field: Record<string, unknown>, fallback: string) {
  return typeof field.label === "string" && field.label.trim() ? field.label : fallback
}

function fieldType(field: Record<string, unknown>) {
  return typeof field.type === "string" && field.type.trim() ? field.type : "text"
}

export function toBaklavaHydrationModel(
  graph: VisualPipelineGraph,
  nodeTypes: VisualPipelineBlock["nodeTypes"],
): VisualPipelineBaklavaHydrationModel {
  const nodeIds = new Set(graph.nodes.map((node) => node.id))
  const incoming = new Set(graph.edges.map((edge) => edge.to))
  const outgoing = new Set(graph.edges.map((edge) => edge.from))
  const warnings = graph.edges.flatMap((edge) => {
    if (!nodeIds.has(edge.from)) return [`Skipped edge ${edge.id} because source node ${edge.from} is missing.`]
    if (!nodeIds.has(edge.to)) return [`Skipped edge ${edge.id} because target node ${edge.to} is missing.`]
    return []
  })

  return {
    nodes: graph.nodes.map((node) => {
      const nodeType = nodeTypes.find((item) => item.type === node.type)
      return {
        id: node.id,
        type: node.type,
        label: node.label,
        category: nodeType?.category,
        position: node.position,
        hasInput: incoming.has(node.id),
        hasOutput: outgoing.has(node.id),
        fields: (nodeType?.fields ?? [])
          .map((field) => ({ field, key: typeof field.key === "string" ? field.key : "" }))
          .filter((item) => item.key)
          .map((item) => ({
            key: item.key,
            label: fieldLabel(item.field, item.key),
            type: fieldType(item.field),
            value: node.data[item.key],
          })),
      }
    }),
    edges: graph.edges
      .filter((edge) => nodeIds.has(edge.from) && nodeIds.has(edge.to))
      .map((edge) => ({ id: edge.id, from: edge.from, to: edge.to })),
    warnings,
  }
}

export function applyBaklavaHydration(input: {
  editor: Editor
  model: VisualPipelineBaklavaHydrationModel
  // true = locked (read-only view); false = editable (add/move/connect/delete).
  readonly: boolean
}): BaklavaHydrationResult {
  const warnings = [...input.model.warnings]
  const nodesById = new Map<string, AbstractNode>()

  for (const connection of [...input.editor.graph.connections]) input.editor.graph.removeConnection(connection)
  for (const node of [...input.editor.graph.nodes]) input.editor.graph.removeNode(node)

  for (const node of input.model.nodes) {
    if (!input.editor.nodeTypes.has(node.type)) {
      input.editor.registerNodeType(
        defineNode({
          type: node.type,
          title: node.label,
          inputs: { input: () => new NodeInterface("Input", null).setHidden(!node.hasInput) },
          outputs: { output: () => new NodeInterface("Output", null).setHidden(!node.hasOutput) },
        }),
        { title: node.label, category: node.category ?? "Pipeline" },
      )
    }
    const nodeInfo = input.editor.nodeTypes.get(node.type)
    if (!nodeInfo) {
      warnings.push(`Skipped node ${node.id} because Baklava node type ${node.type} could not be registered.`)
      continue
    }
    const instance = new nodeInfo.type()
    instance.id = node.id
    instance.title = node.label
    instance.inputs.input?.setHidden(!node.hasInput)
    instance.outputs.output?.setHidden(!node.hasOutput)
    input.editor.graph.addNode(instance)
    setNodePosition(instance, node.position.x, node.position.y)
    nodesById.set(node.id, instance)
  }

  const edgeIds = input.model.edges.flatMap((edge) => {
    const from = nodesById.get(edge.from)?.outputs.output
    const to = nodesById.get(edge.to)?.inputs.input
    if (!from || !to) {
      warnings.push(`Skipped connection ${edge.id} because a Baklava port was missing.`)
      return []
    }
    return input.editor.graph.addConnection(from, to) ? [edge.id] : []
  })

  if (input.readonly) blockBaklavaStructuralEdits(input.editor)

  return {
    warnings,
    nodeIds: [...nodesById.keys()],
    edgeIds,
  }
}

export function blockBaklavaStructuralEdits(editor: Editor) {
  const token = "openanalyst-visual-pipeline-readonly"
  editor.graph.events.beforeAddNode.subscribe(token, (_node, prevent) => prevent())
  editor.graph.events.beforeRemoveNode.subscribe(token, (_node, prevent) => prevent())
  editor.graph.events.beforeAddConnection.subscribe(token, (_connection, prevent) => prevent())
  editor.graph.events.beforeRemoveConnection.subscribe(token, (_connection, prevent) => prevent())
}

// Inverse of applyBaklavaHydration: read the live editor back into a
// VisualPipelineGraph. Structure only (nodes + positions + edges) — node field
// `data` is owned by the block's property panels and merged in on save. Pure
// (duck-typed over the editor graph) so it is unit-testable without a renderer.
export function fromBaklavaEditor(editor: Editor): VisualPipelineGraph {
  const graph = editor.graph as unknown as {
    nodes: Array<{
      id: string
      type: string
      title?: unknown
      position?: { x?: number; y?: number }
      inputs?: Record<string, { id: string }>
      outputs?: Record<string, { id: string }>
    }>
    connections: Array<{ id: string; from: { id: string }; to: { id: string } }>
  }
  const nodeByInterfaceId = new Map<string, string>()
  for (const node of graph.nodes) {
    for (const iface of Object.values(node.inputs ?? {})) nodeByInterfaceId.set(iface.id, node.id)
    for (const iface of Object.values(node.outputs ?? {})) nodeByInterfaceId.set(iface.id, node.id)
  }
  const nodes = graph.nodes.map((node) => {
    const pos = node.position ?? {}
    return {
      id: node.id,
      type: node.type,
      label: typeof node.title === "string" && node.title ? node.title : node.type,
      position: { x: Math.round(pos.x ?? 0), y: Math.round(pos.y ?? 0) },
      data: {} as Record<string, unknown>,
    }
  })
  const edges = graph.connections.flatMap((connection) => {
    const from = nodeByInterfaceId.get(connection.from.id)
    const to = nodeByInterfaceId.get(connection.to.id)
    if (!from || !to) return []
    return [{ id: connection.id, from, to }]
  })
  return { schemaVersion: 1, nodes, edges }
}
